import Button from 'tio-ui/components/button';
import Divider from 'tio-ui/components/divider';
import { fn } from '@ember/helper';
import { not, and } from 'tio-ui/utilities';
import * as v from 'valibot';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import ClaimsFinancialsCalculator from '../../../../utils/tuition-assistance/claims-financials-calculator.ts';
import Component from '@glimmer/component';
import DefinitionList from '../../../ui/definition-list.gts';
import { divide } from 'tio-ui/math';
import errorsForField from '../../../../helpers/errors-for-field.ts';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import inputmask from '../../../../modifiers/inputmask.ts';
import safeFormatNumber from '../../../../helpers/safe-format-number.ts';
import type { ClaimsFinancialsCalculatorTrackedPropName } from '../../../../utils/tuition-assistance/claims-financials-calculator.ts';
import type { TasClaimsFinancials } from '../../../../types/tuition-assistance.ts';
import type Owner from '@ember/owner';
import { PhPencil } from 'ember-phosphor-icons';

export interface TuitionAssistanceApplicationDetailsClaimsFinancialsSignature {
  Args: {
    claimsFinancials: Readonly<TasClaimsFinancials>;
    didUpdate: (updatedValue: TasClaimsFinancials) => void;
    didSave: (updatedValue: TasClaimsFinancials, reason: string) => void;
    isPendingApprovalAndNotReadonly: boolean;
    applicationState: string;
  };
  Element: HTMLElement;
}

const editClaimsFinancialsFormSchema = v.object({
  comment: v.pipe(v.string(), v.nonEmpty()),
});

export default class TuitionAssistanceApplicationDetailsClaimsFinancialsComponent extends Component<TuitionAssistanceApplicationDetailsClaimsFinancialsSignature> {
  @tracked originalClaimsFinancials: TasClaimsFinancials;
  @tracked calculator;
  @tracked isEditable: boolean = false;
  @tracked showEditButton: boolean = false;
  @tracked isEditing: boolean = false;
  @tracked reasonForEdit: string = '';

  constructor(
    owner: Owner,
    args: TuitionAssistanceApplicationDetailsClaimsFinancialsSignature['Args']
  ) {
    super(owner, args);
    this.originalClaimsFinancials = trackedObject(args.claimsFinancials);
    this.calculator = new ClaimsFinancialsCalculator(args.claimsFinancials);
    this.isEditable = !this.inEditableState || false;
    this.showEditButton = !this.inEditableState;
  }

  get showCoursesPreApprovalCalculationMessage() {
    return this.args.applicationState === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL';
  }

  get inEditableState() {
    const editableStates = [
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    ];
    return editableStates.includes(this.args.applicationState);
  }

  get disableInputs() {
    return !this.inEditableState && !this.isEditing;
  }

  get formValidationErrors() {
    const model = {
      comment: this.reasonForEdit,
    };

    const result = v.safeParse(editClaimsFinancialsFormSchema, model);

    if (!result.success) {
      const flattened = v.flatten(result.issues);
      const nested = flattened.nested ?? {};

      // Convert Valibot errors to format compatible with errorsForField helper (Yup format)
      return Object.entries(nested).map(([field, messages]) => ({
        path: field,
        errors: messages ?? [],
        value: model[field as keyof typeof model] as string,
      }));
    }

    return [];
  }

  toggleEdit = () => {
    this.isEditing = !this.isEditing;
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  updateValueByName = (keyName: ClaimsFinancialsCalculatorTrackedPropName, value: string = '0') => {
    this.calculator[keyName] = parseFloat(value) * 100;

    if (this.args.isPendingApprovalAndNotReadonly) {
      this.args.didUpdate(this.calculator.results);
    }
  };

  saveCalculatedValues = () => {
    try {
      this.args.didSave(this.calculator.results, this.reasonForEdit);
      this.toggleEdit();
      this.reasonForEdit = '';
    } catch (e) {
      console.error(e);
    }
  };

  cancel = () => {
    this.toggleEdit();
    this.calculator = new ClaimsFinancialsCalculator(this.originalClaimsFinancials);
    this.args.didUpdate(this.calculator.results);
    this.originalClaimsFinancials = trackedObject(this.args.claimsFinancials);
  };

  // TODO: Refactor this to a more explicit approach similar to that of LimitsSummary registry on the
  // Tuition Assistance service. Relying on auto-tracking in the template to call this method when
  // a new ClaimsFinancials object is saved on the TasApplication is not ideal since it's not explicit.
  // Example: Specific workflow this is needed for is when an approver makes an edit in an adjacent
  // component that changes the course grade or course credit. We need to ensure the calculator is updated.
  initCalculatorFromClaimsFinancials = (claimsFinancials: TasClaimsFinancials) => {
    this.originalClaimsFinancials = trackedObject(claimsFinancials);
    this.calculator = new ClaimsFinancialsCalculator(claimsFinancials);
  };

  <template>
    <div ...attributes>
      {{! TODO: See comment above about refactoring this to a more explicit approach. }}
      {{this.initCalculatorFromClaimsFinancials @claimsFinancials}}
      <div class="tio-flex-tight mt-6 mb-2">
        <h2 class="tio-text-xl-semibold uppercase">
          {{t "common.tuition_assistance.claims_financials.default"}}
        </h2>
        {{#if (and (not this.inEditableState) (not this.isEditing))}}
          <Button
            @appearance="outlined"
            @intent="primary"
            {{on "click" this.toggleEdit}}
            title={{t "common.edit"}}
          >
            <PhPencil @size="20" />
          </Button>
        {{/if}}
      </div>
      <h3 class="font-semibold mb-4 text-tio-gray-700 uppercase">
        {{t "common.tuition_assistance.claims_financials.itemized_bill"}}
      </h3>
      <div class="grid grid-cols-2 gap-x-2 gap-y-4 items-center px-2">
        <label class="font-semibold" for="tuition_charged">
          {{t "common.tuition_assistance.claims_financials.total_tuition_charged"}}
        </label>
        <Input
          id="tuition_charged"
          @value={{divide this.originalClaimsFinancials.CF_TUITION_CHARGED 100}}
          @onChange={{fn this.updateValueByName "CF_TUITION_CHARGED"}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled={{this.disableInputs}}
        />
        <label class="font-semibold" for="discount_amount">
          {{t "common.tuition_assistance.claims_financials.discount_amount"}}
        </label>
        <Input
          id="discount_amount"
          @value={{divide this.originalClaimsFinancials.CF_DISCOUNT_AMOUNT 100}}
          @onChange={{fn this.updateValueByName "CF_DISCOUNT_AMOUNT"}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled={{this.disableInputs}}
        />
        <label class="font-semibold" for="tuition_after_discount">
          {{t "common.tuition_assistance.claims_financials.total_after_discount"}}
        </label>
        <Input
          id="tuition_after_discount"
          @value={{divide this.calculator.CF_TUITION_AFTER_DISCOUNT 100}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled="true"
        />
        <label class="font-semibold" for="fees">
          {{t "common.tuition_assistance.claims_financials.fees"}}
        </label>
        <Input
          id="fees"
          @value={{divide this.originalClaimsFinancials.CF_FEES 100}}
          @onChange={{fn this.updateValueByName "CF_FEES"}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled={{this.disableInputs}}
        />
        <label class="font-semibold" for="invoice_total">
          {{t "common.tuition_assistance.claims_financials.invoice_total"}}
        </label>
        <Input
          id="invoice_total"
          @value={{divide this.calculator.CF_INVOICE_TOTAL 100}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled="true"
        />
      </div>

      <Divider class="my-9" />

      <h3 class="font-semibold mb-4 text-tio-gray-700 uppercase">
        {{t "common.tuition_assistance.claims_financials.proof_of_payment"}}
      </h3>
      <div class="grid grid-cols-2 gap-x-2 gap-y-4 items-center px-2">
        <label class="font-semibold" for="employee_pop_amount">
          {{t "common.tuition_assistance.claims_financials.employee_pop_amount"}}
        </label>
        <Input
          id="employee_pop_amount"
          @value={{divide this.originalClaimsFinancials.CF_EMPLOYEE_POP_AMOUNT 100}}
          @onChange={{fn this.updateValueByName "CF_EMPLOYEE_POP_AMOUNT"}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled={{this.disableInputs}}
        />

        <label class="font-semibold" for="loan_pop_amount">
          {{t "common.tuition_assistance.claims_financials.loan_amount"}}
        </label>
        <Input
          id="loan_pop_amount"
          @value={{divide this.originalClaimsFinancials.CF_LOAN_POP_AMOUNT 100}}
          @onChange={{fn this.updateValueByName "CF_LOAN_POP_AMOUNT"}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled={{this.disableInputs}}
        />

        <label class="font-semibold" for="grant_pop_amount">
          {{t "common.tuition_assistance.claims_financials.grant_scholarship_amount"}}
        </label>
        <Input
          id="grant_pop_amount"
          @value={{divide this.originalClaimsFinancials.CF_GRANT_POP_AMOUNT 100}}
          @onChange={{fn this.updateValueByName "CF_GRANT_POP_AMOUNT"}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled={{this.disableInputs}}
        />

        <label class="font-semibold" for="other_pop_amount">
          {{t "common.tuition_assistance.claims_financials.other_pop_amount"}}
        </label>
        <Input
          id="other_pop_amount"
          @value={{divide this.originalClaimsFinancials.CF_OTHER_POP_AMOUNT 100}}
          @onChange={{fn this.updateValueByName "CF_OTHER_POP_AMOUNT"}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled={{this.disableInputs}}
        />

        <label class="font-semibold" for="total_pop_amount">
          {{t "common.tuition_assistance.claims_financials.total_pop_amount"}}
        </label>
        <Input
          id="total_pop_amount"
          @value={{divide this.calculator.CF_TOTAL_POP_AMOUNT 100}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled="true"
        />

        <label class="font-semibold" for="unpaid_balance">
          {{t "common.tuition_assistance.claims_financials.unpaid_balance"}}
        </label>
        <Input
          id="unpaid_balance"
          @value={{divide this.calculator.CF_UNPAID_BALANCE 100}}
          {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2" autoUnmask=true}}
          disabled="true"
        />
      </div>

      {{#if this.isEditing}}
        <Textarea
          @value={{this.reasonForEdit}}
          @onChange={{this.updateReasonForEdit}}
          @errors={{errorsForField "comment" schemaErrors=this.formValidationErrors}}
          @label={{t "common.comment"}}
          class="my-4 mx-2"
        />
        <div class="flex flex-wrap gap-4 justify-center items-center my-6">
          <Button
            @appearance="outlined"
            @intent="primary"
            class="w-48"
            {{on "click" this.saveCalculatedValues}}
          >
            {{t "common.save"}}
          </Button>
          <Button class="w-48" @appearance="outlined" @intent="danger" {{on "click" this.cancel}}>
            {{t "common.cancel"}}
          </Button>
        </div>
      {{/if}}
      <Divider class="my-9" />

      <h2 class="tio-text-heading mb-2 text-tio-gray-700 uppercase">
        {{t "common.tuition_assistance.claims_financials.approval_calculations"}}
      </h2>
      <DefinitionList as |options|>
        <div class={{options.wrappingClass}}>
          <options.dt class="font-medium">
            {{t "common.tuition_assistance.claims_financials.applicable_grant"}}
          </options.dt>
          <options.dd>
            {{safeFormatNumber
              (divide this.calculator.CF_APPLICABLE_GRANT 100)
              style="currency"
              currency="USD"
            }}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt class="font-medium">
            {{t "common.tuition_assistance.claims_financials.graded_tuition_amount"}}
          </options.dt>
          <options.dd>
            {{safeFormatNumber
              (divide this.calculator.CF_GRADED_TUITION 100)
              style="currency"
              currency="USD"
            }}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt class="font-medium">
            {{t "common.tuition_assistance.claims_financials.percent_total_cost"}}
          </options.dt>
          <options.dd>
            {{safeFormatNumber this.calculator.CF_PERCENT_TOTAL_COST style="percent"}}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt class="font-medium">
            {{t "common.tuition_assistance.claims_financials.grant_distribution"}}
          </options.dt>
          <options.dd>
            {{safeFormatNumber
              (divide this.calculator.CF_GRANT_DISTRIBUTION 100)
              style="currency"
              currency="USD"
            }}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt class="font-medium">
            {{t "common.tuition_assistance.claims_financials.benefit_percent"}}
          </options.dt>
          <options.dd>
            {{safeFormatNumber
              this.calculator.CF_BENEFIT_FRACTIONAL_PERCENT_APPLIED
              style="percent"
            }}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt class="font-medium">
            {{t "common.tuition_assistance.claims_financials.benefit_percent_x_graded_tuition"}}
          </options.dt>
          <options.dd>
            {{safeFormatNumber
              (divide this.calculator.CF_BENEFIT_PERCENT_OF_GRADED_TUITION 100)
              style="currency"
              currency="USD"
            }}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt class="font-medium">
            {{t
              "common.tuition_assistance.claims_financials.graded_tuition_amount_minus_grant_distribution"
            }}
          </options.dt>
          <options.dd>
            {{safeFormatNumber
              (divide this.calculator.CF_GRADED_TUITION_LESS_GRANT_DISTRIBUTION 100)
              style="currency"
              currency="USD"
            }}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt class="font-bold">
            {{t "common.tuition_assistance.claims_financials.approved_total"}}
          </options.dt>
          <options.dd class="font-bold">
            {{safeFormatNumber
              (divide this.calculator.CF_REIMBURSEMENT_TOTAL 100)
              style="currency"
              currency="USD"
            }}
          </options.dd>
        </div>
        {{#if this.showCoursesPreApprovalCalculationMessage}}
          <div class={{options.wrappingClass}}>
            <options.dt class="text-tio-gray-600 text-center">
              {{t
                "common.tuition_assistance.claims_financials.courses_pre_approval_calculation_message"
              }}
            </options.dt>
          </div>
        {{/if}}
      </DefinitionList>
    </div>
  </template>
}
